import React, { useEffect, useState } from "react";
import { Box, Typography, Divider, TextField } from "@mui/material";
import CustomDialog from "../../../../components/Dialog/CustomDialog";
import { postRequest } from "../../../../Apis";
import { campaignEndpoints } from "../../../../utils/constants/httpConstants";
import parse from "html-react-parser";
import EventEmitter from "../../../../utils/emitter/EventEmitter";
import { EMITTER_ERROR } from "../../../../utils/constants";
import { decompressString, decryptEmail } from "../../../../utils/helpers";
import pako from "pako";
import HtmlEditor from "../../../../components/Editor/HtmlEditor";
import IconComponent from "../../../../components/Icon/IconComponent";
import { ICON_LABELS } from "../../../../utils/constants/UI/iconLabels";
import { BRAND } from "../../../../utils/constants/colors";
import Button from "../../../../components/Buttons/Button";
import SkeletonLoader from "../../../../components/Loaders/SkeletonLoader";

const ViewAiContentDialog = ({
  open,
  onClose,
  prospect,
  step, // The specific step object
  stepNumber, // NEW: The step number (1, 2, 3...) passed from parent
  campaignId,
  sequenceId,
  email,
  navigate,
}) => {
  const [loading, setLoading] = useState(false);
  const [aiData, setAiData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingStepInfo, setEditingStepInfo] = useState({
    id: null,
    type: null,
  });
  const [saving, setSaving] = useState(false);
  const [editedSubject, setEditedSubject] = useState("");
  const [editedBody, setEditedBody] = useState("");
  const [editedTitle, setEditedTitle] = useState("");
  const [editedNotes, setEditedNotes] = useState("");
  const [editorKey, setEditorKey] = useState(0); // Force re-render of editor

  const fetchAiContent = async () => {
    if (!open) return;

    // 1. Extract IDs
    const record_id = prospect?.record_id || prospect?._id;

    // 2. Validation
    // if (!record_id) {
    //   console.error("Missing record_id");
    //   return;
    // }

    setLoading(true);
    try {
      const payload = {
        campaign_id: campaignId,
        // record_id: record_id,
        sequence_id: sequenceId,
        email: email,
      };

      const response = await postRequest(
        navigate,
        campaignEndpoints.viewAiGeneratedcontent,
        payload,
      );

      if (response?.data?.data) {
        setAiData(response.data.data);
      }
    } catch (error) {
      EventEmitter.emit(
        EMITTER_ERROR,
        error?.data?.message || "Failed to fetch content",
      );
      setAiData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setAiData(null);
      fetchAiContent();
    }
  }, [open, prospect, step, campaignId, sequenceId, navigate]);

  // Helper to detect if content looks compressed (base64-like pattern)
  const isCompressed = (data) => {
    if (!data || typeof data !== "string") return false;
    // Check if it looks like base64 compressed data (no spaces, special chars like =)
    // and doesn't contain common readable patterns
    return (
      /^[A-Za-z0-9+/=]+$/.test(data) && data.length > 50 && !data.includes(" ")
    );
  };

  // Helper to handle decompression and URL decoding
  const getDecompressedContent = (data) => {
    if (!data) return "";

    // If content looks like plain text (not compressed), handle it directly
    if (!isCompressed(data)) {
      // Convert \n to <br> for HTML rendering
      return data.replace(/\n/g, "<br>");
    }

    // Otherwise, try to decompress
    try {
      let decompressed = decompressString(data);

      if (!decompressed) {
        try {
          const compressedUint8Array = Uint8Array.from(atob(data), (c) =>
            c.charCodeAt(0),
          );
          const decompressedUint8Array = pako.inflate(compressedUint8Array);
          decompressed = new TextDecoder().decode(decompressedUint8Array);
        } catch (manualError) {
          // If decompression fails, treat as plain text
          return data.replace(/\n/g, "<br>");
        }
      }

      try {
        const decoded = decodeURIComponent(decompressed);
        // Also handle \n in decompressed content
        return decoded.replace(/\n/g, "<br>");
      } catch (decodeError) {
        return decompressed.replace(/\n/g, "<br>");
      }
    } catch (e) {
      // Fallback: treat as plain text
      return data.replace(/\n/g, "<br>");
    }
  };

  // Handle edit mode
  const handleEditClick = (type, data) => {
    setEditingStepInfo({ id: data.step_id, type });
    if (type === "email") {
      const subject = data.subject?.replace(/^Subject:\s*/i, "");
      const body = getDecompressedContent(data.body);
      setEditedSubject(subject);
      setEditedBody(body || "");
    } else if (type === "call" || type === "todo") {
      const notes = getDecompressedContent(data.notes);
      setEditedTitle(data.title);
      setEditedNotes(notes || "");
    }

    setEditorKey((prev) => prev + 1);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingStepInfo({ id: null, type: null });
    setEditedSubject("");
    setEditedBody("");
    setEditedTitle("");
    setEditedNotes("");
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      let payload = {
        campaign_id: campaignId,
        email: email,
        step_id: editingStepInfo.id,
        step_type: editingStepInfo.type,
      };

      if (editingStepInfo.type === "email") {
        payload.subject = editedSubject;
        payload.body = editedBody;
      } else {
        payload.title = editedTitle;
        payload.notes = editedNotes;
      }

      const response = await postRequest(
        navigate,
        campaignEndpoints.editaigeneratedcontent,
        payload,
      );

      if (response?.data?.success) {
        setIsEditing(false);
        setEditingStepInfo({ id: null, type: null });
        fetchAiContent();
      }
    } catch (error) {
      EventEmitter.emit(
        EMITTER_ERROR,
        error?.data?.message || "Failed to update content",
      );
    } finally {
      setSaving(false);
    }
  };

  // Helper function to generate name from email
  const generateNameFromEmail = (email) => {
    if (!email) return "";
    const [localPart] = email.split("@");
    return localPart
      .split(/[._-]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Get the contact display name
  const getContactDisplayName = () => {
    if (!prospect) return "AI Generated Content";
    return (
      prospect.full_name ||
      generateNameFromEmail(prospect.email) ||
      prospect.email ||
      "AI Generated Content"
    );
  };

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      width="40vw"
      header={{
        heading: {
          label: getContactDisplayName(),
        },
      }}
    >
      <Box>
        {loading ? (
          <Box p={2}>
            {/* Email Skeleton Structure */}
            <Box mb={3}>
              <SkeletonLoader
                type="text"
                skeletonWidth="30%"
                skeletonHeight={24}
                stackHeight="auto"
              />
              <Box mt={2}>
                <SkeletonLoader
                  type="text"
                  skeletonWidth="60%"
                  skeletonHeight={20}
                  stackHeight="auto"
                />
              </Box>
              <Box mt={2}>
                <SkeletonLoader
                  type="rectangular"
                  skeletonWidth="100%"
                  skeletonHeight={1}
                  stackHeight="auto"
                />
              </Box>
              <Box mt={2}>
                <SkeletonLoader
                  type="text"
                  skeletonWidth="100%"
                  skeletonHeight={18}
                  stackHeight="auto"
                />
              </Box>
              <Box mt={1}>
                <SkeletonLoader
                  type="text"
                  skeletonWidth="95%"
                  skeletonHeight={18}
                  stackHeight="auto"
                />
              </Box>
              <Box mt={1}>
                <SkeletonLoader
                  type="text"
                  skeletonWidth="90%"
                  skeletonHeight={18}
                  stackHeight="auto"
                />
              </Box>
              <Box mt={1}>
                <SkeletonLoader
                  type="text"
                  skeletonWidth="85%"
                  skeletonHeight={18}
                  stackHeight="auto"
                />
              </Box>
              <Box mt={1}>
                <SkeletonLoader
                  type="text"
                  skeletonWidth="92%"
                  skeletonHeight={18}
                  stackHeight="auto"
                />
              </Box>
              <Box mt={1}>
                <SkeletonLoader
                  type="text"
                  skeletonWidth="85%"
                  skeletonHeight={18}
                  stackHeight="auto"
                />
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="95%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="80%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="85%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="85%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="95%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="80%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="85%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="85%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="95%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="80%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="85%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
                <Box mt={1}>
                  <SkeletonLoader
                    type="text"
                    skeletonWidth="85%"
                    skeletonHeight={18}
                    stackHeight="auto"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        ) : aiData?.content ? (
          <Box>
            {/* --- EMAIL LOGIC (Match by step_number) --- */}
            {(() => {
              const activeEmail = aiData.content.emails?.find(
                (e) => e.step_number === stepNumber,
              );
              if (activeEmail) {
                return (
                  <Box>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        Email Content
                      </Typography>
                      {!isEditing && step?.status !== "Delivered" && (
                        <Box
                          onClick={() => handleEditClick("email", activeEmail)}
                          sx={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <IconComponent
                            iconLabel={ICON_LABELS.EDIT}
                            color={BRAND.PRIMARY}
                            fontSize={"20px"}
                          />
                        </Box>
                      )}
                    </Box>

                    {isEditing ? (
                      <Box>
                        <TextField
                          fullWidth
                          label="Subject"
                          value={editedSubject}
                          onChange={(e) => setEditedSubject(e.target.value)}
                          sx={{ mb: 2 }}
                          size="small"
                        />
                        <Box
                          sx={{
                            border: "1px solid #ddd",
                            borderRadius: 1,
                            minHeight: 300,
                            p: 1,
                            "& .ql-editor": {
                              fontSize: "18px !important",
                              lineHeight: "1.7",
                              fontFamily: "inherit",
                            },
                            "& .ql-editor p, & .ql-editor li, & .ql-editor span, & .ql-editor div":
                              {
                                fontSize: "18px !important",
                              },
                            "& .ql-editor strong, & .ql-editor b": {
                              fontSize: "18px !important",
                            },
                          }}
                        >
                          <HtmlEditor
                            key={`email-editor-${editorKey}`}
                            previosData={editedBody}
                            getData={setEditedBody}
                            editorHeight="280px"
                            initialFontSize={{ id: "3", label: "Medium" }}
                          />
                        </Box>
                        <Box
                          display="flex"
                          gap={1}
                          justifyContent="flex-end"
                          mt={2}
                        >
                          {/* CUSTOM BUTTON USAGE: CANCEL */}
                          <Button
                            variant="ghost"
                            size="sm36"
                            label="Cancel"
                            onClick={handleCancelEdit}
                          />
                          {/* CUSTOM BUTTON USAGE: SAVE */}
                          <Button
                            variant="primary"
                            size="sm36"
                            label="Save"
                            onClick={handleSaveEdit}
                            loading={saving}
                            disabled={saving}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "bold", mb: 1 }}
                        >
                          Subject:{" "}
                          {getDecompressedContent(activeEmail.subject)?.replace(
                            /^Subject:\s*/i,
                            "",
                          )}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box className="email-body-wrapper">
                          {parse(getDecompressedContent(activeEmail.body))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              }
              return null;
            })()}

            {/* --- CALL LOGIC (Match by step_number) --- */}
            {(() => {
              const activeCall = aiData.content.calls?.find(
                (c) => c.step_number === stepNumber,
              );
              if (activeCall) {
                return (
                  <Box>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        Call Script
                      </Typography>
                      {!isEditing && step?.status !== "Delivered" && (
                        <Box
                          onClick={() => handleEditClick("call", activeCall)}
                          sx={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <IconComponent
                            iconLabel={ICON_LABELS.EDIT}
                            color={BRAND.PRIMARY}
                            fontSize={"20px"}
                          />
                        </Box>
                      )}
                    </Box>

                    {isEditing ? (
                      <Box>
                        <TextField
                          fullWidth
                          label="Title"
                          value={editedTitle}
                          onChange={(e) => setEditedTitle(e.target.value)}
                          sx={{ mb: 2 }}
                          size="small"
                        />
                        <Box
                          sx={{
                            border: "1px solid #ddd",
                            borderRadius: 1,
                            minHeight: 300,
                            p: 1,
                            "& .ql-editor": {
                              fontSize: "18px !important",
                              lineHeight: "1.7",
                              fontFamily: "inherit",
                            },
                            "& .ql-editor p, & .ql-editor li, & .ql-editor span, & .ql-editor div":
                              {
                                fontSize: "18px !important",
                              },
                            "& .ql-editor strong, & .ql-editor b": {
                              fontSize: "18px !important",
                            },
                          }}
                        >
                          <HtmlEditor
                            key={`call-editor-${editorKey}`}
                            previosData={editedNotes}
                            getData={setEditedNotes}
                            editorHeight="280px"
                            initialFontSize={{ id: "3", label: "Medium" }}
                          />
                        </Box>
                        <Box
                          display="flex"
                          gap={1}
                          justifyContent="flex-end"
                          mt={2}
                        >
                          {/* CUSTOM BUTTON USAGE: CANCEL */}
                          <Button
                            variant="ghost"
                            size="sm36"
                            label="Cancel"
                            onClick={handleCancelEdit}
                          />
                          {/* CUSTOM BUTTON USAGE: SAVE */}
                          <Button
                            variant="primary"
                            size="sm36"
                            label="Save"
                            onClick={handleSaveEdit}
                            loading={saving}
                            disabled={saving}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "600", mb: 1 }}
                        >
                          {activeCall.title}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box className="call-notes-wrapper">
                          {parse(getDecompressedContent(activeCall.notes))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              }
              return null;
            })()}

            {/* --- TODO LOGIC (Match by step_number) --- */}
            {(() => {
              const activeTodo = aiData.content.todos?.find(
                (t) => t.step_number === stepNumber,
              );
              if (activeTodo) {
                return (
                  <Box>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={2}
                    >
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        Todo Task
                      </Typography>
                      {!isEditing && step?.status !== "Delivered" && (
                        <Box
                          onClick={() => handleEditClick("todo", activeTodo)}
                          sx={{
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <IconComponent
                            iconLabel={ICON_LABELS.EDIT}
                            color={BRAND.PRIMARY}
                            fontSize={"20px"}
                          />
                        </Box>
                      )}
                    </Box>

                    {isEditing ? (
                      <Box>
                        <TextField
                          fullWidth
                          label="Title"
                          value={editedTitle}
                          onChange={(e) => setEditedTitle(e.target.value)}
                          sx={{ mb: 2 }}
                          size="small"
                        />
                        <Box
                          sx={{
                            border: "1px solid #ddd",
                            borderRadius: 1,
                            minHeight: 300,
                            p: 1,
                            "& .ql-editor": {
                              fontSize: "18px !important",
                              lineHeight: "1.7",
                              fontFamily: "inherit",
                            },
                            "& .ql-editor p, & .ql-editor li, & .ql-editor span, & .ql-editor div":
                              {
                                fontSize: "18px !important",
                              },
                            "& .ql-editor strong, & .ql-editor b": {
                              fontSize: "18px !important",
                            },
                          }}
                        >
                          <HtmlEditor
                            key={`todo-editor-${editorKey}`}
                            previosData={editedNotes}
                            getData={setEditedNotes}
                            editorHeight="40vh"
                          />
                        </Box>
                        <Box
                          display="flex"
                          gap={1}
                          justifyContent="flex-end"
                          mt={2}
                        >
                          {/* CUSTOM BUTTON USAGE: CANCEL */}
                          <Button
                            variant="ghost"
                            size="sm36"
                            label="Cancel"
                            onClick={handleCancelEdit}
                          />
                          {/* CUSTOM BUTTON USAGE: SAVE */}
                          <Button
                            variant="primary"
                            size="sm36"
                            label="Save"
                            onClick={handleSaveEdit}
                            loading={saving}
                            disabled={saving}
                          />
                        </Box>
                      </Box>
                    ) : (
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: "600", mb: 1 }}
                        >
                          {activeTodo.title}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box className="todo-notes-wrapper">
                          {parse(getDecompressedContent(activeTodo.notes))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              }
              return null;
            })()}

            {/* --- FALLBACK --- */}
            {(() => {
              const hasEmail = aiData.content.emails?.some(
                (e) => e.step_number === stepNumber,
              );
              const hasCall = aiData.content.calls?.some(
                (c) => c.step_number === stepNumber,
              );
              const hasTodo = aiData.content.todos?.some(
                (t) => t.step_number === stepNumber,
              );

              if (!hasEmail && !hasCall && !hasTodo) {
                return (
                  <Typography textAlign="center" color="textSecondary" mt={2}>
                    No AI content found for this specific step.
                  </Typography>
                );
              }
              return null;
            })()}
          </Box>
        ) : (
          <Typography>No content available.</Typography>
        )}
      </Box>
    </CustomDialog>
  );
};

export default ViewAiContentDialog;
